import { ForumNotifications } from '../guild/ForumNotifications'
import { NsfwGate } from './NsfwGate'
import { nsfwAcceptedFor, rememberNsfw } from '@/lib/nsfw'
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { Archive, ArchiveRestore, ArrowLeft, Bell, BellOff, Ellipsis, Hash, ImagePlay, ListChecks, Lock, LogOut, MessageSquare, Mic, Send, Pin, Search, Smile, Sticker as StickerIcon, Trash2, Unlock, UserPlus, Users, X, type LucideIcon } from 'lucide-react'
import { canSelectForDeletion, MAX_BULK_DELETE } from '@/lib/bulkDelete'
import { useMessageSelection } from './useMessageSelection'
import { BulkDeleteModal, MessageSelectionControls } from './MessageSelection'
import selectionStyles from './MessageSelection.module.css'
import { MediaPicker } from './MediaPicker'
import type { StickerLock } from './StickerGrid'
import { usePicker } from './usePicker'
import { PinnedPanel } from './PinnedPanel'
import { customEmojiToken, insertToken, searchUnicode } from '@/lib/emoji'
import { useGifsEnabled } from '@/lib/gifsApi'
import { emojiImageUrl } from '@/lib/markdown'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { attachmentLimit, messageLimit } from '@/lib/upload'
import { uploadGif } from '@/lib/sendGif'
import { useGuildStore } from '@/store/guilds'
import { guildMuted, mutedAncestor } from '@/lib/muting'
import type { SettingsPatch } from '@/lib/guildApi'

import { compareIds, displayName, typingUsers, useSocialStore } from '@/store/social'
import { describeError, publicUser } from '@/lib/auth'
import { ApiError } from '@/lib/api'
import { AttachmentStrip } from './AttachmentStrip'
import { filesFromPaste, useUploads } from './useUploads'
import { P, channelPermissions, has, roleColor, threadPermissions } from '@/lib/permissions'
import { socialApi } from '@/lib/socialApi'
import { renderMarkdown } from '@/lib/markdown'
import { mentionNames } from '@/lib/mentions'
import { matches, readable, type Suggestion, type Trigger } from '@/lib/autocomplete'
import { avatarUrl, memberAvatarUrl } from '@/lib/avatar'
import type { PickEnv } from '@/lib/commands'
import { commandInvocation, mergeEphemeral } from '@/lib/interactions'
import { cx } from '@/lib/cx'
import { ChannelType, MessageType, type Member, type Message, type User } from '@/types/api'
import { MessageRow } from './MessageRow'
import { SystemMessage } from './SystemMessage'
import { MentionPopup } from './MentionPopup'
import { useMentions } from './useMentions'
import { CommandForm, SlashPopup } from './SlashCommands'
import { useComponentEnv, type CommandSource } from './useCommandIndex'
import { useSlashCommands } from './useSlashCommands'
import { FormatToolbar } from './FormatToolbar'
import { useFormatToolbar } from './useFormatToolbar'
import { useChatReading } from './useChatReading'
import { useAutosize } from '@/lib/useAutosize'
import { draftPicks, useDraft, useDraftsStore } from '@/store/drafts'
import { formatDay } from '@/lib/format'
import { archiveLabel, memberName } from '@/lib/guildUi'
import { Avatar } from '../Avatar'
import { ReportModal } from '../ReportModal'
import { ComposerPlus } from './ComposerPlus'
import { CreatePollModal } from './CreatePollModal'
import { VoiceRecordBar } from './VoiceRecorder'
import { useVoiceRecorder } from './useVoiceRecorder'
import { canRecordVoice } from '@/lib/voiceMessage'
import { ThreadModal } from './ThreadModal'
import { JumpToPresent } from './JumpToPresent'
import { useMessageJump } from './useMessageJump'
import { ConfirmModal } from '../guild/ConfirmModal'
import { VoiceRoom } from '../voice/VoiceRoom'
import type { ReportTarget } from '@/lib/reportApi'
import chatStyles from '../ChatView.module.css'
import dm from './DMChat.module.css'
import styles from './ChannelChat.module.css'
import { coarsePointer, useIsMobile } from '@/lib/mobile'
import { MobileBack } from '@/components/MobileBack'
import { ContextMenu, MenuItem, MenuSeparator } from '@/components/ContextMenu'

const GROUP_WINDOW = 7 * 60 * 1000
const SYSTEM_TYPES = new Set<number>([MessageType.RecipientAdd, MessageType.RecipientRemove, MessageType.ChannelNameChange, MessageType.ChannelPinned, MessageType.UserJoin, MessageType.ThreadCreated, MessageType.PollResult])

/** Чат текстового канала сервера: те же сообщения, что в ЛС, плюс права, ники и цвета ролей. */
interface ChannelChatProps {
  guildId: string
  channelId: string
  embedded?: boolean
  onClose?: () => void
}

export function ChannelChat(props: ChannelChatProps) {
  const channel = useGuildStore((s) => s.channels[props.guildId]?.[props.channelId])
  if (!channel) return <main className={chatStyles.chat} />
  if (channel?.type === ChannelType.GuildVoice && !props.embedded) {
    return <VoiceRoom guildId={props.guildId} channelId={props.channelId} />
  }
  return <MessageChannelChat {...props} />
}

function MessageChannelChat({ guildId, channelId, embedded = false, onClose }: ChannelChatProps) {
  const mobile = useIsMobile()
  const headerMenuButton = useRef<HTMLButtonElement>(null)
  const [headerMenu, setHeaderMenu] = useState<{ x: number; y: number } | null>(null)
  const closeHeaderMenu = useCallback(() => setHeaderMenu(null), [])
  const me = useAuthStore((s) => s.user)
  const guild = useGuildStore((s) => s.guilds[guildId])
  const allGuilds = useGuildStore((s) => s.guilds)
  const channel = useGuildStore((s) => s.channels[guildId]?.[channelId])
  const [nsfwAccepted, setNsfwAccepted] = useState(() => nsfwAcceptedFor(channelId))
  const members = useGuildStore((s) => s.members[guildId])
  const showMembers = useAppStore((s) => s.showMembers)
  const toggleMembers = useAppStore((s) => s.toggleMembers)
  const openSettings = useAppStore((s) => s.openSettings)
  const searchQuery = useAppStore((s) => s.searchQuery)
  const setSearchQuery = useAppStore((s) => s.setSearchQuery)
  const setJumpTarget = useAppStore((s) => s.setJumpTarget)
  const setView = useAppStore((s) => s.setView)
  const threadsPanel = useAppStore((s) => s.threadsPanel)
  const setThreadsPanel = useAppStore((s) => s.setThreadsPanel)
  const updateThread = useGuildStore((s) => s.updateThread)
  const joinThread = useGuildStore((s) => s.joinThread)
  const leaveThread = useGuildStore((s) => s.leaveThread)
  const deleteChannel = useGuildStore((s) => s.deleteChannel)
  const settings = useGuildStore((s) => s.settings[guildId])
  const updateSettings = useGuildStore((s) => s.updateSettings)
  const channels = useGuildStore((s) => s.channels[guildId])
  const messages = useSocialStore((s) => s.messages[channelId])
  const stale = useSocialStore((s) => channelId in s.stale)
  // ответы приложений «только вам» — в ленте среди остальных, но не в истории канала
  const ephemeral = useSocialStore((s) => s.ephemeral[channelId])
  const hasOlder = useSocialStore((s) => s.hasOlder[channelId])
  const loading = useSocialStore((s) => s.loading[channelId])
  const typingMap = useSocialStore((s) => s.typing[channelId])
  const currentUserId = useSocialStore((s) => s.currentUserId)
  const typingIds = useMemo(() => typingUsers(typingMap, currentUserId), [typingMap, currentUserId])
  const { loadMessages, sendMessage, retryMessage, editMessage, deleteMessage, suppressEmbeds, ack, typingPing, toggleReaction, pinMessage } = useSocialStore()
  const [report, setReport] = useState<ReportTarget | null>(null)
  const [pollOpen, setPollOpen] = useState(false)

  const [draft, setDraft] = useDraft(channelId)
  const [replyTo, setReplyTo] = useState<Message | null>(null)
  const [error, setError] = useState<string | null>(null)
  const voice = useVoiceRecorder(channelId, setError)
  const plus = useAuthStore((s) => s.user?.premium_type === 2)
  const uploads = useUploads(channelId, attachmentLimit(Boolean(plus), guild?.premium_tier ?? 0))
  const fileInput = useRef<HTMLInputElement | null>(null)
  const [dragging, setDragging] = useState(false)
  const [searchDraft, setSearchDraft] = useState(searchQuery ?? '')
  // на телефоне строки поиска в шапке нет места: значок открывает её поверх шапки
  const [searchOpen, setSearchOpen] = useState(false)
  const searchInput = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    if (searchOpen) searchInput.current?.focus()
  }, [searchOpen])
  const [threadModal, setThreadModal] = useState<Message | null | undefined>(undefined) // undefined — закрыто, null — ветка без сообщения
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [starter, setStarter] = useState<{ id: string; message: Message | null } | null>(null)
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  useEffect(() => {
    if (embedded && channel?.type !== ChannelType.PublicThread) textareaRef.current?.focus()
  }, [embedded, channel?.type])
  const stickToBottom = useRef(true)
  // колокольчик снимает то, из-за чего канал молчит: свой mute, mute категории или всего сервера
  const mutedBy = mutedAncestor(settings, channelId, channels)
  const muted = mutedBy !== null || guildMuted(settings)
  const bellLabel = !muted ? 'Заглушить канал' : mutedBy === channelId ? 'Включить уведомления канала' : mutedBy ? 'Включить уведомления категории' : 'Включить уведомления сервера'
  // последнее сообщение канала: окно после перехода по ссылке может быть от него оторвано
  const latestId = useCallback(() => useGuildStore.getState().channels[guildId]?.[channelId]?.last_message_id, [guildId, channelId])
  // до эффекта первой загрузки: окно вокруг сообщения должно начать грузиться раньше неё
  const { highlight, detached, toPresent, jumping } = useMessageJump(channelId, scrollerRef, stickToBottom, latestId)

  const self: Member | undefined = me ? (members?.[me.id] ?? { user: publicUser(me), nick: null, avatar: null, roles: [], joined_at: '', premium_since: null, deaf: false, mute: false, pending: false, communication_disabled_until: null }) : undefined
  const isThread = channel?.type === ChannelType.PublicThread
  const parent = isThread && channel?.parent_id ? channels?.[channel.parent_id] : undefined
  const parentId = parent?.id
  const perms = guild && self ? (isThread ? threadPermissions(guild, self, parent) : channelPermissions(guild, self, channel)) : 0n
  const canSend = has(perms, P.SEND_MESSAGES)
  const canReadHistory = has(perms, P.READ_MESSAGE_HISTORY)
  const shown = useMemo(() => embedded && !canReadHistory ? [] : mergeEphemeral(messages ?? [], ephemeral), [messages, ephemeral, embedded, canReadHistory])
  const canManage = has(perms, P.MANAGE_MESSAGES)
  const canSelectMessages = canManage && canReadHistory && has(perms, P.VIEW_CHANNEL) && (!(channel?.nsfw || parent?.nsfw) || nsfwAccepted)
  const selection = useMessageSelection(channelId, shown, canSelectMessages)
  const canThreads = channel?.type === ChannelType.GuildText && has(perms, P.CREATE_PUBLIC_THREADS)
  const canManageThreads = has(perms, P.MANAGE_THREADS)
  const canAttach = has(perms, P.ATTACH_FILES)
  const canPoll = has(perms, P.SEND_POLLS)
  const meta = channel?.thread_metadata
  const archived = Boolean(meta?.archived)
  const lockedOut = archived && Boolean(meta?.locked) && !canManageThreads
  const isThreadOwner = Boolean(isThread && channel?.owner_id && channel.owner_id === me?.id)
  const panelOpen = threadsPanel?.channelId === (isThread ? parentId : channelId)
  const timedOut = Boolean(self?.communication_disabled_until && new Date(self.communication_disabled_until) > new Date())

  const reading = useChatReading(me?.id, channelId, scrollerRef, stickToBottom, detached, jumping, canReadHistory && (!(channel?.nsfw || parent?.nsfw) || nsfwAccepted), (err) => setError(err instanceof Error ? err.message : describeError(err).message))
  const goLatest = () => void reading.toLatest(toPresent).catch((err) => setError(err instanceof Error ? err.message : describeError(err).message))
  const reply = useCallback((message: Message) => {
    setReplyTo(message)
    textareaRef.current?.focus({ preventScroll: true })
  }, [])
  const sending = useRef(false)
  const [sendingMessage, setSendingMessage] = useState(false)

  const users = useMemo(() => {
    const map: Record<string, User> = {}
    for (const m of Object.values(members ?? {})) map[m.user.id] = { ...m.user, ...(m.nick ? { global_name: m.nick } : {}) }
    if (me && !map[me.id]) map[me.id] = publicUser(me)
    return map
  }, [members, me])
  const roleName = useMemo(() => (id: string) => {
    const r = guild?.roles.find((x) => x.id === id)
    return r ? { name: r.name, color: r.color ? `#${r.color.toString(16).padStart(6, '0')}` : undefined } : undefined
  }, [guild])

  // подсказки: @участник, @роль, #канал
  const suggest = useCallback(
    (tr: Trigger): Suggestion[] => {
      if (tr.kind === ':') {
        const custom: Suggestion[] = Object.values(allGuilds)
          .flatMap((g) => (g.emojis ?? []).map((e) => ({ e, guild: g })))
          .filter(({ e }) => e.name.toLowerCase().includes(tr.query.toLowerCase()))
          .slice(0, 6)
          .map(({ e, guild: g }) => ({ key: `custom:${e.id}`, kind: 'emoji' as const, label: `:${e.name}:`, hint: g.name, emojiUrl: e.url ?? emojiImageUrl(e.id, e.animated), insert: customEmojiToken(e), text: `:${e.name}:` }))
        const unicode: Suggestion[] = searchUnicode(tr.query, 6).map((it) => ({ key: `u:${it.char}`, kind: 'emoji' as const, label: it.char, hint: it.keys.split(' ')[0], emoji: it.char, insert: it.char }))
        return [...custom, ...unicode]
      }
      if (tr.kind === '#') {
        return Object.values(channels ?? {})
          .filter((c) => c.type === ChannelType.GuildText && matches(tr.query, c.name))
          .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'ru'))
          .map((c) => ({ key: c.id, kind: 'channel', label: c.name ?? '', insert: `<#${c.id}>`, text: readable('channel', c.name ?? '') }))
      }
      const users: Suggestion[] = Object.values(members ?? {})
        .filter((m) => matches(tr.query, m.user.username, m.nick, m.user.global_name))
        .sort((a, b) => memberName(a).localeCompare(memberName(b), 'ru'))
        .slice(0, 8)
        .map((m) => ({ key: m.user.id, kind: 'user', label: memberName(m), hint: `@${m.user.username}`, insert: `<@${m.user.id}>`, text: readable('user', memberName(m)), bot: Boolean(m.user.bot), avatar: memberAvatarUrl(guildId, m) }))
      // роль бота (managed) не предлагаем: в ней только сам бот, и в подсказке выходил его двойник
      const roles: Suggestion[] = (guild?.roles ?? [])
        .filter((r) => r.id !== guild?.id && !r.managed && (r.mentionable || has(perms, P.MENTION_EVERYONE)) && matches(tr.query, r.name))
        .map((r) => ({ key: r.id, kind: 'role', label: r.name, hint: 'роль', color: r.color ? `#${r.color.toString(16).padStart(6, '0')}` : undefined, insert: `<@&${r.id}>`, text: readable('role', r.name) }))
      const everyone: Suggestion[] = has(perms, P.MENTION_EVERYONE) && matches(tr.query, 'everyone', 'here') ? [{ key: 'everyone', kind: 'role', label: 'everyone', hint: 'все на сервере', insert: '@everyone' }] : []
      return [...users, ...roles, ...everyone]
    },
    [channels, members, guild, guildId, perms, allGuilds],
  )
  const mentions = useMentions({ textarea: textareaRef, draft, setDraft, suggest, picked: draftPicks(channelId) })
  // кого и что можно выбрать в опциях команд и в списках под сообщениями ботов
  const pickEnv = useMemo<PickEnv>(
    () => ({
      users: [
        ...Object.values(members ?? {}).map((m) => ({ id: m.user.id, name: memberName(m), username: m.user.username, bot: Boolean(m.user.bot), avatar: m.user.avatar })),
        ...(me && !members?.[me.id] ? [{ id: me.id, name: displayName(me), username: me.username, avatar: me.avatar }] : []),
      ],
      // роли — сверху вниз, как в настройках сервера; @everyone — тоже роль
      roles: [...(guild?.roles ?? [])]
        .sort((a, b) => b.position - a.position)
        .map((r) => ({ id: r.id, name: r.id === guild?.id ? '@everyone' : r.name, color: r.color ? `#${r.color.toString(16).padStart(6, '0')}` : undefined })),
      channels: Object.values(channels ?? {}).map((c) => ({ id: c.id, name: c.name ?? '', type: c.type })),
    }),
    [members, me, guild, channels],
  )
  const commandSource = useMemo<CommandSource>(() => ({ guildId }), [guildId])
  const slash = useSlashCommands({
    source: commandSource,
    enabled: canSend && !lockedOut && has(perms, P.USE_APPLICATION_COMMANDS),
    guildId,
    channelId,
    draft,
    setDraft,
    textarea: textareaRef,
    env: pickEnv,
    maxFileSize: attachmentLimit(Boolean(plus), guild?.premium_tier ?? 0),
    onSend: () => {
      stickToBottom.current = true
    },
  })
  const interact = useComponentEnv({ source: commandSource, guildId, env: pickEnv })
  // поле растёт раньше, чем встаёт панель форматирования: она меряет поле уже новой высоты
  useAutosize(textareaRef, draft)
  const toolbarRef = useRef<HTMLDivElement | null>(null)
  const format = useFormatToolbar(textareaRef, { toolbarRef, hidden: mentions.items.length > 0 || slash.picker !== null, onApply: mentions.close })
  const customEmoji = useMemo(() => {
    const list = Object.values(allGuilds)
    const cur = list.find((g) => g.id === guildId)
    return [...(cur ? [cur] : []), ...list.filter((g) => g.id !== guildId)].map((g) => ({ guild: g.name, emojis: g.emojis ?? [] }))
  }, [allGuilds, guildId])
  const [pinsOpen, setPinsOpen] = useState(false)
  const picker = usePicker()
  const [sendingGif, setSendingGif] = useState(false)
  const gifsOn = useGifsEnabled()
  const sendSticker = useSocialStore((s) => s.sendSticker)

  /** Эмодзи из панели уходит в поле ввода на место курсора, а не отправляется сразу. */
  const putEmoji = (e: { id: string | null; name: string; animated?: boolean }) => {
    const el = textareaRef.current
    const token = e.id ? customEmojiToken({ id: e.id, name: e.name, ...(e.animated ? { animated: true } : {}) }) : e.name
    const at = el ? el.selectionStart : draft.length
    const to = el ? el.selectionEnd : draft.length
    const { text, caret } = insertToken(draft, at, to, token)
    setDraft(text)
    if (coarsePointer) picker.close()
    el?.focus({ preventScroll: true })
    requestAnimationFrame(() => {
      el?.setSelectionRange(caret, caret)
    })
  }
  // стикеры других серверов в канале сервера — с Кличат Плюсом и правом «Использовать чужие стикеры»
  const stickerLock: StickerLock = !plus ? 'plus' : !has(perms, P.USE_EXTERNAL_STICKERS) ? 'forbidden' : null
  const stickerGroups = useMemo(() => {
    const list = Object.values(allGuilds).filter((g) => g.stickers?.length)
    const cur = list.find((g) => g.id === guildId)
    return [
      ...(cur ? [{ guild: cur.name, stickers: cur.stickers ?? [] }] : []),
      ...list.filter((g) => g.id !== guildId).map((g) => ({ guild: g.name, stickers: g.stickers ?? [], locked: stickerLock !== null })),
    ]
  }, [allGuilds, guildId, stickerLock])

  // после переподключения — перечитать конец истории: пропущенное за перерыв само не придёт
  useEffect(() => {
    if (canReadHistory && (!messages || stale) && (channel?.type === ChannelType.GuildText || channel?.type === ChannelType.GuildVoice || channel?.type === ChannelType.PublicThread)) {
      void loadMessages(channelId).catch((err) => setError(describeError(err).message))
    }
  }, [channelId, messages, stale, loadMessages, channel?.type, canReadHistory])

  // стартовое сообщение ветки живёт в родительском канале
  useEffect(() => {
    if (!isThread || !parentId) return
    let cancelled = false
    socialApi
      .message(parentId, channelId)
      .then((m) => {
        if (!cancelled) setStarter({ id: channelId, message: m.type === MessageType.ThreadCreated ? null : m })
      })
      .catch(() => {
        if (!cancelled) setStarter({ id: channelId, message: null })
      })
    return () => {
      cancelled = true
    }
  }, [isThread, parentId, channelId])

  useEffect(() => {
    if (!canReadHistory || !messages?.length || !reading.atLatest) return
    // берём самое свежее из двух: показанного сообщения и указателя канала. Указатель
    // может смотреть на удалённое сообщение — тогда по одному лишь списку «непрочитано» не снять
    const newest = [...messages].reverse().find((m) => !m.pending)?.id
    const pointer = channel?.last_message_id ?? undefined
    const lastId = newest && pointer ? (compareIds(pointer, newest) > 0 ? pointer : newest) : (newest ?? pointer)
    const mark = () => {
      if (document.visibilityState === 'visible') void ack(channelId, lastId)
    }
    mark()
    document.addEventListener('visibilitychange', mark)
    return () => document.removeEventListener('visibilitychange', mark)
  }, [channelId, messages, ack, channel?.last_message_id, canReadHistory, reading.atLatest])

  const onScroll = () => {
    const el = scrollerRef.current
    if (!el) return
    reading.onScroll()
    if (!reading.ready) return
    if (el.scrollTop < 120 && hasOlder && !loading && messages?.length) {
      const first = messages.find((m) => !m.pending)
      if (first) {
        void loadMessages(channelId, first.id).catch((err) => setError(describeError(err).message))
      }
    }
  }

  const submit = async () => {
    if (sending.current || reading.busy) return
    const content = draft.trim()
    if ((!content && uploads.items.length === 0) || !canSend || lockedOut) return
    if (uploads.items.length > 0 && !canAttach) {
      setError('У вас нет права прикреплять файлы в этом канале')
      return
    }
    setError(null)
    const reply = replyTo
    const sentFiles = uploads.items.map((file) => file.id)
    sending.current = true
    setSendingMessage(true)
    try {
      await reading.toLatest(toPresent)
      const refs = await uploads.upload(channelId)
      if ((useDraftsStore.getState().drafts[channelId] ?? '') === draft) setDraft('')
      setReplyTo((current) => current?.id === reply?.id ? null : current)
      uploads.clear(sentFiles)
      await sendMessage(channelId, mentions.toServer(content), reply ?? undefined, refs)
    } catch (err) {
      setError(err instanceof Error && !(err instanceof ApiError) ? err.message : describeError(err).message)
    } finally {
      sending.current = false
      setSendingMessage(false)
    }
  }

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (slash.onTextKeyDown(e)) return
    if (mentions.onKeyDown(e)) return
    if (format.onKeyDown(e)) return
    if (e.key === 'Enter' && !e.shiftKey && !coarsePointer && !e.nativeEvent.isComposing) {
      e.preventDefault()
      void submit()
    }
    if (e.key === 'Escape' && replyTo) {
      e.preventDefault()
      setReplyTo(null)
    }
  }

  const submitSearch = (e: FormEvent) => {
    e.preventDefault()
    const q = searchDraft.trim()
    setSearchQuery(q || null)
    // результаты на телефоне открываются слоем поверх чата; строка поиска своё отработала
    if (q) setSearchOpen(false)
  }
  const toggleMute = () => {
    const patch: SettingsPatch = !muted ? { channel_overrides: { [channelId]: { muted: true } } } : mutedBy ? { channel_overrides: { [mutedBy]: { muted: false } } } : { muted: false }
    void updateSettings(guildId, patch).catch((err: unknown) => setError(describeError(err).message))
  }
  const channelRef = (id: string) => {
    const c = channels?.[id]
    return c ? { name: c.name ?? '', onClick: () => setView({ kind: 'guild', guildId, channelId: id }) } : undefined
  }
  const openThread = (id: string) => setView({ kind: 'guild', guildId, channelId: id })
  const togglePanel = () => setThreadsPanel(panelOpen ? null : { guildId, channelId: isThread && parentId ? parentId : channelId })
  const run = (p: Promise<unknown>) => p.catch((err: unknown) => setError(describeError(err).message))
  // действия с веткой: на широком экране — значки в шапке, на телефоне — меню «⋯» (значки не влезали и растягивали экран)
  const forumThread = parent?.type === ChannelType.GuildForum
  const [forumNotifications, setForumNotifications] = useState(false)
  if (forumNotifications && (!forumThread || !canReadHistory || !has(perms, P.VIEW_CHANNEL))) setForumNotifications(false)
  const threadActions: { label: string; icon: LucideIcon; onClick: () => void; disabled?: boolean; danger?: boolean }[] = !isThread
    ? []
    : [
        ...(forumThread
          ? [{ label: 'Уведомления темы', icon: Bell, onClick: () => setForumNotifications(true), disabled: !canReadHistory }]
          : [channel.member
            ? { label: 'Покинуть ветку', icon: LogOut, onClick: () => void run(leaveThread(guildId, channelId)) }
            : { label: 'Присоединиться к ветке', icon: UserPlus, disabled: archived, onClick: () => void run(joinThread(guildId, channelId)) }]),
        ...(isThreadOwner || canManageThreads
          ? [{ label: archived ? 'Вернуть из архива' : 'В архив', icon: archived ? ArchiveRestore : Archive, disabled: lockedOut, onClick: () => void run(updateThread(channelId, archived ? { archived: false, locked: false } : { archived: true })) }]
          : []),
        ...(canManageThreads
          ? [
              { label: meta?.locked ? (forumThread ? 'Открыть тему' : 'Открыть ветку') : (forumThread ? 'Закрыть тему' : 'Закрыть ветку'), icon: meta?.locked ? Unlock : Lock, onClick: () => void run(updateThread(channelId, meta?.locked ? { archived: false, locked: false } : { archived: true, locked: true })) },
              { label: forumThread ? 'Удалить тему' : 'Удалить ветку', icon: Trash2, danger: true, onClick: () => setConfirmDelete(true) },
            ]
          : []),
      ]
  const [threadMenu, setThreadMenu] = useState<{ x: number; y: number } | null>(null)

  if (!guild || !channel) return <main className={chatStyles.chat} />
  const name = channel.name ?? ''

  const Root = embedded ? 'section' : 'main'

  return (
    <Root
      id={embedded ? `${forumThread ? 'forum-thread' : 'voice-chat'}-${channelId}` : undefined}
      className={cx(chatStyles.chat, embedded && chatStyles.embedded)}
      aria-label={forumThread ? `Тема ${name}` : embedded ? `Чат канала ${name}` : isThread ? `Ветка ${name}` : `Канал ${name}`}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !e.defaultPrevented && selection.active) { e.preventDefault(); e.stopPropagation(); selection.finish(); return }
        if (embedded && e.key === 'Escape' && !e.defaultPrevented) {
          e.stopPropagation()
          onClose?.()
        }
      }}
      onDragOver={(e) => {
        if (canAttach && e.dataTransfer.types.includes('Files')) {
          e.preventDefault()
          setDragging(true)
        }
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        setDragging(false)
        if (canAttach && e.dataTransfer.files.length) {
          e.preventDefault()
          uploads.add(e.dataTransfer.files)
        }
      }}
    >
      {dragging ? <div className={dm.dropOverlay} aria-hidden="true">Отпустите, чтобы прикрепить файлы</div> : null}
      <header className={chatStyles.header} data-search={searchOpen ? '' : undefined}>
        {embedded ? forumThread ? <button type="button" className={cx(chatStyles.iconButton, styles.forumReturn)} aria-label="К списку тем" onClick={onClose}><ArrowLeft size={22} /></button> : null : <MobileBack />}
        {isThread || embedded ? <MessageSquare size={24} className={chatStyles.hash} /> : <Hash size={24} className={chatStyles.hash} />}
        <h1 className={cx(chatStyles.title, chatStyles.titleText)} data-tooltip={name}>{embedded && !forumThread ? `Чат · ${name}` : name}</h1>
        {isThread && parent && parent.type !== ChannelType.GuildForum ? (
          <button type="button" className={cx(styles.parentLink, chatStyles.wideOnly)} data-tooltip="Перейти в канал" onClick={() => openThread(parent.id)}>в #{parent.name}</button>
        ) : null}
        {isThread && archived ? <span className={cx(styles.archivedBadge, chatStyles.wideOnly)}>{meta?.locked ? 'в архиве, закрыта' : 'в архиве'}</span> : null}
        {!isThread && channel.nsfw ? <span className={styles.nsfwBadge} data-tooltip="Канал 18+">18+</span> : null}
        {!isThread && channel.topic ? <span className={chatStyles.topic} data-tooltip={channel.topic}>{channel.topic}</span> : null}
        <div className={chatStyles.tools}>
          {canSelectMessages ? <button type="button" className={cx(chatStyles.iconButton, chatStyles.desktopOnly, selection.active && styles.toolActive)} data-tooltip="Выбрать сообщения" aria-label="Выбрать сообщения" aria-pressed={selection.active} onClick={() => { setPinsOpen(false); if (selection.active) selection.finish(); else selection.start() }}><ListChecks size={22} /></button> : null}
          {isThread ? (
            <>
              {threadActions.map(({ label, icon: Icon, onClick, disabled }) => (
                <button key={label} type="button" className={cx(chatStyles.iconButton, chatStyles.wideOnly, chatStyles.desktopOnly)} data-tooltip={label} aria-label={label} disabled={disabled} onClick={onClick}><Icon size={20} /></button>
              ))}
              <button
                type="button"
                className={cx(chatStyles.iconButton, chatStyles.narrowOnly, chatStyles.desktopOnly)}
                data-tooltip={forumThread ? 'Действия с темой' : 'Действия с веткой'}
                aria-label={forumThread ? 'Действия с темой' : 'Действия с веткой'}
                aria-haspopup="menu"
                aria-expanded={Boolean(threadMenu)}
                onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect()
                  setThreadMenu(threadMenu ? null : { x: r.right, y: r.bottom + 4 })
                }}
              >
                <Ellipsis size={22} />
              </button>
              {threadMenu ? (
                <ContextMenu x={threadMenu.x} y={threadMenu.y} align="right" label={forumThread ? 'Действия с темой' : 'Действия с веткой'} onClose={() => setThreadMenu(null)}>
                  {parent ? (
                    <>
                      <MenuItem icon={<ArrowLeft size={16} />} label={`Перейти в #${parent.name ?? ''}`} onClick={() => { setThreadMenu(null); openThread(parent.id) }} />
                      <MenuSeparator />
                    </>
                  ) : null}
                  {threadActions.map(({ label, icon: Icon, onClick, disabled, danger }) => (
                    <MenuItem key={label} icon={<Icon size={16} />} label={label} disabled={disabled ?? false} danger={danger ?? false} onClick={() => { setThreadMenu(null); onClick() }} />
                  ))}
                </ContextMenu>
              ) : null}
            </>
          ) : (
            <button type="button" className={cx(chatStyles.iconButton, chatStyles.desktopOnly, muted && styles.toolMuted)} data-tooltip={bellLabel} aria-label={bellLabel} aria-pressed={muted} onClick={toggleMute}>
              {muted ? <BellOff size={22} /> : <Bell size={22} />}
            </button>
          )}
          <button type="button" className={cx(chatStyles.iconButton, chatStyles.desktopOnly, pinsOpen && styles.toolActive)} data-tooltip="Закреплённые сообщения" aria-label="Закреплённые сообщения" aria-pressed={pinsOpen} onClick={() => setPinsOpen((v) => !v)}>
            <Pin size={22} />
          </button>
          {pinsOpen ? <PinnedPanel channelId={channelId} onJump={(id) => setJumpTarget({ channelId, messageId: id })} onClose={() => setPinsOpen(false)} /> : null}
          {(channel.type === ChannelType.GuildVoice || forumThread) ? null : <button type="button" className={cx(chatStyles.iconButton, chatStyles.desktopOnly, panelOpen && styles.toolActive)} data-tooltip="Ветки канала" aria-label="Ветки канала" aria-pressed={panelOpen} onClick={togglePanel}>
            <MessageSquare size={22} />
          </button>}
          <button type="button" className={cx(chatStyles.iconButton, chatStyles.searchToggle)} data-tooltip="Поиск" aria-label="Поиск" onClick={() => setSearchOpen(true)}>
            <Search size={22} />
          </button>
          <form className={chatStyles.search} onSubmit={submitSearch} role="search">
            <input
              type="search"
              placeholder="Поиск"
              className={chatStyles.searchInput}
              aria-label="Поиск по серверу"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape' && searchOpen) setSearchOpen(false) }}
              ref={searchInput}
            />
            <Search size={16} className={chatStyles.searchIcon} />
            {searchOpen ? (
              <button type="button" className={chatStyles.searchClose} data-tooltip="Закрыть поиск" aria-label="Закрыть поиск" onClick={() => setSearchOpen(false)}>
                <X size={20} />
              </button>
            ) : null}
          </form>
          {embedded ? <button type="button" className={chatStyles.iconButton} data-tooltip={forumThread ? "Закрыть тему" : "Закрыть чат"} aria-label={forumThread ? "Закрыть тему" : "Закрыть чат"} onClick={onClose}><X size={22} /></button> : <button type="button" className={cx(chatStyles.iconButton, chatStyles.desktopOnly, showMembers && !searchQuery && styles.toolActive)} data-tooltip={showMembers ? 'Скрыть участников' : 'Показать участников'} aria-label="Список участников" aria-pressed={showMembers} onClick={toggleMembers}>
            <Users size={22} />
          </button>}
          {mobile ? <button ref={headerMenuButton} type="button" className={chatStyles.iconButton} aria-label="Ещё действия в канале" aria-haspopup="menu" aria-expanded={Boolean(headerMenu)} onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect()
            setHeaderMenu(headerMenu ? null : { x: r.right, y: r.bottom + 4 })
          }}><Ellipsis size={22} /></button> : null}
        </div>
        {mobile && headerMenu ? (
          <ContextMenu {...headerMenu} align="right" anchorRef={headerMenuButton} label="Действия в канале" onClose={closeHeaderMenu}>
            {!embedded ? <MenuItem icon={<Users size={16} />} label="Список участников" onClick={() => { closeHeaderMenu(); toggleMembers() }} /> : null}
            {canSelectMessages ? <MenuItem icon={<ListChecks size={16} />} label={selection.active ? 'Отменить выбор сообщений' : 'Выбрать сообщения'} onClick={() => { closeHeaderMenu(); setPinsOpen(false); if (selection.active) selection.finish(); else selection.start() }} /> : null}
            {!isThread ? <MenuItem icon={muted ? <BellOff size={16} /> : <Bell size={16} />} label={bellLabel} onClick={() => { closeHeaderMenu(); toggleMute() }} /> : null}
            <MenuItem icon={<Pin size={16} />} label="Закреплённые сообщения" onClick={() => { closeHeaderMenu(); setPinsOpen(v => !v) }} />
            {channel.type !== ChannelType.GuildVoice && !forumThread ? <MenuItem icon={<MessageSquare size={16} />} label="Ветки канала" onClick={() => { closeHeaderMenu(); togglePanel() }} /> : null}
            {isThread ? <>
              <MenuSeparator />
              {parent ? <MenuItem icon={<ArrowLeft size={16} />} label={`Перейти в #${parent.name ?? ''}`} onClick={() => { closeHeaderMenu(); openThread(parent.id) }} /> : null}
              {threadActions.map(({ label, icon: Icon, onClick, disabled, danger }) => <MenuItem key={label} icon={<Icon size={16} />} label={label} disabled={disabled ?? false} danger={danger ?? false} onClick={() => { closeHeaderMenu(); onClick() }} />)}
            </> : null}
          </ContextMenu>
        ) : null}
      </header>

      {isThread && parent?.type === ChannelType.GuildForum && !embedded ? <button type="button" className={styles.forumBack} onClick={() => openThread(parent.id)}><ArrowLeft size={18} />К темам · {parent.name}</button> : null}
      {selection.active ? <MessageSelectionControls selection={selection} /> : null}
      {selection.confirm ? <BulkDeleteModal key={channelId} channelId={channelId} channelName={name} messages={selection.confirm} onCancel={selection.closeConfirm} onDeleted={selection.finish} /> : null}

      {(channel.nsfw || parent?.nsfw) && !nsfwAccepted ? (
        <NsfwGate name={name} allowed={me?.nsfw_allowed !== false} onAccept={() => { rememberNsfw(channelId); setNsfwAccepted(true) }} />
      ) : (
      <>
      <div className={chatStyles.messageViewport}>
        <div className={cx(chatStyles.messages, 'scroller')} ref={reading.ref} onScroll={onScroll}>
          <div className={chatStyles.messagesInner}>
            {!hasOlder && isThread ? (
              <div className={chatStyles.welcome}>
                <span className={chatStyles.welcomeIcon}><MessageSquare size={42} /></span>
                <h2 className={chatStyles.welcomeTitle}>{name}</h2>
                <p className={chatStyles.welcomeText}>
                  {forumThread ? 'Тема в форуме ' : 'Ветка в #'}{parent?.name ?? '…'}{channel.owner_id && members?.[channel.owner_id] ? `, начал(а) ${memberName(members[channel.owner_id]!)}` : ''}.
                  {archived ? ' Сейчас в архиве.' : ` Без активности уйдёт в архив через ${archiveLabel(meta?.auto_archive_duration)}.`}
                </p>
                {starter?.id === channelId && starter.message ? (
                  <div className={styles.starter}>
                    <div className={styles.starterHead}>
                      <Avatar initial={displayName(starter.message.author).slice(0, 1).toUpperCase()} color="#5865F2" size={24} src={avatarUrl(starter.message.author.id, starter.message.author.avatar)} decoration={(users[starter.message.author.id] ?? starter.message.author).avatar_decoration_data} />
                      <span>{users[starter.message.author.id] ? displayName(users[starter.message.author.id]!) : displayName(starter.message.author)}</span>
                    </div>
                    <div className={styles.starterBody}>{renderMarkdown(starter.message.content, { userName: mentionNames(starter.message, users), role: roleName, channel: channelRef })}</div>
                  </div>
                ) : null}
              </div>
            ) : null}
            {!hasOlder && !isThread ? (
              <div className={chatStyles.welcome}>
                <span className={chatStyles.welcomeIcon}>{embedded ? <MessageSquare size={42} /> : <Hash size={42} />}</span>
                <h2 className={chatStyles.welcomeTitle}>{embedded ? `Чат канала «${name}»` : `Добро пожаловать в #${name}!`}</h2>
                {embedded ? null : <p className={chatStyles.welcomeText}>Это начало канала #{name}.{channel.topic ? ` ${channel.topic}` : ''}</p>}
              </div>
            ) : null}
            {loading && !messages?.length ? <p className={dm.loading}>Загружаем историю…</p> : null}
            {embedded && !canReadHistory ? <p className={dm.loading}>У вас нет права читать историю этого канала</p> : null}
            {shown.map((m, i, all) => {
              const prev = all[i - 1]
              const sameDay = prev && new Date(prev.timestamp).toDateString() === new Date(m.timestamp).toDateString()
              const system = SYSTEM_TYPES.has(m.type)
              // ответ на команду начинает группу: над ним строка «использовал(а) /команда», как у ответа
              const continuation = Boolean(
                prev && sameDay && !system && !SYSTEM_TYPES.has(prev.type) && prev.author.id === m.author.id && (!m.webhook_id || prev.author.username === m.author.username) && !m.referenced_message && !m.message_reference && !commandInvocation(m) &&
                  new Date(m.timestamp).getTime() - new Date(prev.timestamp).getTime() < GROUP_WINDOW,
              )
              const author = members?.[m.author.id]
              return (
                <div key={m.id} data-message-id={m.id} className={cx(highlight === m.id && chatStyles.highlight)}>
                  {!sameDay ? <div className={chatStyles.dateDivider}><span>{formatDay(new Date(m.timestamp))}</span></div> : null}
                  {system ? (
                    <SystemMessage message={m} users={users} guildId={guildId} onOpenThread={openThread} onJump={(id) => setJumpTarget({ channelId, messageId: id })} />
                  ) : (
                    <div className={cx(selection.active && selectionStyles.row, selection.ids.has(m.id) && selectionStyles.selected)}>
                    {selection.active && canSelectForDeletion(m) ? <label className={selectionStyles.checkbox}><input type="checkbox" aria-label={`Выбрать сообщение ${displayName(m.author)}: ${(m.content || 'без текста').slice(0, 70)}`} checked={selection.ids.has(m.id)} disabled={!selection.ids.has(m.id) && selection.ids.size >= MAX_BULK_DELETE} onChange={() => selection.select(m)} /></label> : null}
                    <MessageRow guildId={guildId}
                      message={m}
                      continuation={continuation}
                      mine={m.author.id === me?.id}
                      canDelete={canManage}
                      canEditPublication={guild?.owner_id === me?.id && Boolean(guild?.features.some(f => ['GAME_HUB_DOTA2', 'GAME_HUB_CS2', 'GAME_HUB_DEADLOCK', 'GAME_HUB_MINECRAFT', 'GAME_HUB_GTA5RP', 'GAME_HUB_ROBLOX'].includes(f))) && m.type === 0 && m.author.id === '1550657676906590300' && !m.webhook_id && !m.components?.length}
                      onSelectForDeletion={canSelectMessages && canSelectForDeletion(m) ? selection.select : undefined}
                      // цвет — по нынешним ролям участника: роли в сообщении — снимок на момент отправки
                      nameColor={roleColor(guild.roles, author?.roles ?? m.member?.roles ?? [])}
                      roleName={roleName}
                      channelRef={channelRef}
                      users={users}
                      onReply={reply} swipeReplyEnabled={canSend && !lockedOut && !timedOut && !selection.active}
                      onEdit={(msg, content) => editMessage(channelId, msg.id, content)}
                      onDelete={(msg) => deleteMessage(channelId, msg.id)}
                      onRetry={(msg) => msg.nonce && void retryMessage(channelId, msg.nonce)}
                      onReport={(msg) => setReport({ type: 'message', id: msg.id, label: `сообщение ${users[msg.author.id] ? displayName(users[msg.author.id]!) : displayName(msg.author)}` })}
                      onSuppress={(msg) => suppressEmbeds(channelId, msg.id)}
                      onThread={canThreads ? (msg) => setThreadModal(msg) : undefined}
                      onOpenThread={openThread}
                      onReact={has(perms, P.ADD_REACTIONS) || m.reactions?.length ? (msg, e) => toggleReaction(channelId, msg, e).catch((err) => setError(describeError(err).message)) : undefined}
                      onPin={canManage ? (msg, pinned) => pinMessage(channelId, msg.id, pinned).catch((err) => setError(describeError(err).message)) : undefined}
                      customEmoji={customEmoji}
                      channelId={channelId}
                      onJump={(id) => setJumpTarget({ channelId, messageId: id })}
                      interact={interact}
                    />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {canReadHistory && (!(channel?.nsfw || parent?.nsfw) || nsfwAccepted) && reading.showLatest ? <JumpToPresent onClick={goLatest} disabled={reading.busy || Boolean(loading)} /> : null}
      </div>

      <div className={chatStyles.typing} data-active={typingIds.length ? '' : undefined} aria-live="polite">
        {typingIds.length ? (
          <>
            <span className={chatStyles.dots} aria-hidden="true"><i /><i /><i /></span>
            <span>
              <strong>{typingIds.map((id) => (members?.[id] ? memberName(members[id]!) : users[id] ? displayName(users[id]!) : 'Кто-то')).join(', ')}</strong>
              {typingIds.length === 1 ? ' печатает…' : ' печатают…'}
            </span>
          </>
        ) : null}
      </div>
      </>
      )}

      <div className={dm.composerWrap}>
        {sendingGif ? <p className={dm.sending}>Отправляем гифку…</p> : null}
        {slash.error ?? error ?? uploads.error ? <p className={dm.error} role="alert">{slash.error ?? error ?? uploads.error}</p> : null}
        <AttachmentStrip items={uploads.items} onRemove={uploads.remove} disabled={sendingMessage} />
        <MentionPopup items={mentions.items} active={mentions.active} title={mentions.trigger?.kind === '#' ? 'Каналы' : 'Участники и роли'} onPick={mentions.pick} onHover={mentions.setActive} />
        <SlashPopup slash={slash} />
        {format.toolbar ? <FormatToolbar ref={toolbarRef} text={draft} {...format.toolbar} /> : null}
        {picker.tab ? (
          <div className={dm.stickerAnchor}>
            <MediaPicker
              tab={picker.tab}
              onTab={picker.select}
              stickers={stickerGroups}
              stickerLock={stickerLock}
              onGetPlus={() => {
                picker.close()
                openSettings('premium')
              }}
              emoji={customEmoji}
              gifs={gifsOn}
              onClose={picker.close}
              onSticker={(s) => { picker.close(); void reading.toLatest(toPresent).then(() => sendSticker(channelId, s)).catch((err) => setError(err instanceof Error ? err.message : describeError(err).message)) }}
              onGif={(g) => {
                picker.close()
                setSendingGif(true)
                // гифку кладём вложением: в чате должна остаться она сама, а не ссылка
                reading.toLatest(toPresent).then(() => uploadGif(channelId, g, attachmentLimit(Boolean(plus), guild?.premium_tier ?? 0)))
                  .then((refs) => sendMessage(channelId, '', undefined, refs))
                  .catch((err) => setError(err instanceof Error ? err.message : describeError(err).message))
                  .finally(() => setSendingGif(false))
              }}
              onEmoji={putEmoji}
            />
          </div>
        ) : null}
        {voice.rec ? <VoiceRecordBar rec={voice.rec} busy={voice.busy} onSend={() => void voice.send()} onCancel={voice.cancel} /> : null}
        {replyTo ? (
          <div className={dm.replyBar}>
            <span>Ответ пользователю <strong>{users[replyTo.author.id] ? displayName(users[replyTo.author.id]!) : displayName(replyTo.author)}</strong></span>
            <button type="button" aria-label="Отменить ответ" onClick={() => setReplyTo(null)}><X size={16} /></button>
          </div>
        ) : null}
        <form className={cx(chatStyles.composer, (replyTo || uploads.items.length > 0) && dm.composerWithReply, (!canSend || lockedOut) && styles.composerDisabled)} onSubmit={(e) => { e.preventDefault(); if (slash.form) void slash.form.submit(); else void submit() }}>
          {slash.form ? (
            <CommandForm slash={slash} form={slash.form} />
          ) : (
            <ComposerPlus className={chatStyles.attach} disabled={!canSend || lockedOut || sendingMessage} canAttach={canAttach} canPoll={canPoll} onUpload={() => fileInput.current?.click()} onPoll={() => setPollOpen(true)} onFiles={uploads.add} onGif={gifsOn ? () => picker.toggle('gif') : undefined} onSticker={() => picker.toggle('sticker')} />
          )}
          <input ref={fileInput} type="file" multiple hidden aria-hidden="true" onChange={(e) => { if (e.target.files) uploads.add(e.target.files); e.target.value = '' }} />
          {/* в режиме команды поле ввода прячется, но остаётся в DOM: его хуки держат ссылку на него */}
          <textarea
            ref={textareaRef}
            rows={1}
            hidden={slash.form !== null}
            {...slash.textAria}
            className={chatStyles.input}
            placeholder={
              timedOut ? 'Вы в таймауте и пока не можете писать'
                : lockedOut ? (forumThread ? 'Тема закрыта модератором' : 'Ветка закрыта модератором')
                  : !canSend ? (isThread ? 'У вас нет прав писать в ветках' : 'У вас нет прав писать в этом канале')
                    : archived ? (forumThread ? 'Написать в тему — она вернётся из архива' : 'Написать в ветку — она вернётся из архива')
                      : forumThread ? `Написать в тему «${name}»` : isThread ? `Написать в ветку «${name}»` : embedded ? `Написать в «${name}»` : `Написать в #${name}`
            }
            aria-label="Текст сообщения"
            value={draft}
            maxLength={messageLimit(Boolean(plus))}
            disabled={!canSend || lockedOut}
            onChange={(e) => {
              setDraft(e.target.value)
              mentions.refresh(e.target.value, e.target.selectionStart)
              slash.onTextChange(e.target.value)
              if (e.target.value.trim()) typingPing(channelId)
            }}
            onFocus={slash.onTextFocus}
            onKeyDown={onKeyDown}
            onSelect={format.onSelect}
            onPaste={(e) => {
              const files = filesFromPaste(e)
              if (files.length) {
                e.preventDefault()
                uploads.add(files)
                return
              }
              format.onPaste(e)
            }}
            onBlur={() => {
              setTimeout(mentions.close, 150)
              format.onBlur()
            }}
          />
        {slash.form ? null : (
        <div className={chatStyles.composerTools}>
          {canRecordVoice && (canSend && !lockedOut && has(perms, P.SEND_VOICE_MESSAGES)) ? (
            <button type="button" className={cx(chatStyles.tool, voice.recording && chatStyles.toolActive, (draft.trim() || uploads.items.length > 0) && chatStyles.desktopTool)} data-tooltip={voice.recording ? 'Отправить голосовое' : 'Голосовое сообщение'} aria-label={voice.recording ? 'Отправить голосовое' : 'Записать голосовое'} aria-pressed={voice.recording} disabled={voice.busy} onClick={() => void (voice.recording ? voice.send() : voice.start())}>
              <Mic size={22} />
            </button>
          ) : null}
          {gifsOn ? (
            <button type="button" data-picker-trigger className={cx(chatStyles.tool, chatStyles.desktopTool)} data-tooltip="Гифки" aria-label="Гифки" aria-expanded={picker.tab === 'gif'} disabled={!canSend || lockedOut} onClick={() => picker.toggle('gif')}>
              <ImagePlay size={22} />
            </button>
          ) : null}
          <button type="button" data-picker-trigger className={cx(chatStyles.tool, chatStyles.desktopTool)} data-tooltip="Стикеры" aria-label="Стикеры" aria-expanded={picker.tab === 'sticker'} disabled={!canSend || lockedOut} onClick={() => picker.toggle('sticker')}>
            <StickerIcon size={22} />
          </button>
          <button type="button" data-picker-trigger className={chatStyles.tool} data-tooltip="Эмодзи" aria-label="Эмодзи" aria-expanded={picker.tab === 'emoji'} disabled={!canSend || lockedOut} onClick={() => { if (coarsePointer) textareaRef.current?.blur(); picker.toggle('emoji') }}>
            <Smile size={22} />
          </button>
            {(draft.trim() || uploads.items.length > 0) ? <button type="submit" className={cx(chatStyles.tool, chatStyles.mobileSend)} aria-label="Отправить сообщение" disabled={sendingMessage || reading.busy || !canSend || lockedOut || timedOut} onMouseDown={(e) => e.preventDefault()}><Send size={22} /></button> : null}
        </div>
        )}
        </form>
      </div>
      {report ? <ReportModal target={report} onClose={() => setReport(null)} /> : null}
      {pollOpen ? <CreatePollModal channelId={channelId} custom={customEmoji} onClose={() => setPollOpen(false)} /> : null}
      {forumNotifications && forumThread && canReadHistory ? <ForumNotifications key={channelId} thread={channel} onClose={() => setForumNotifications(false)} /> : null}
      {threadModal !== undefined ? (
        <ThreadModal
          channelId={isThread && parentId ? parentId : channelId}
          channelName={(isThread ? parent?.name : channel.name) ?? ''}
          message={threadModal}
          onClose={() => setThreadModal(undefined)}
          onCreated={(th) => { setThreadModal(undefined); openThread(th.id) }}
        />
      ) : null}
      {confirmDelete ? (
        <ConfirmModal
          title={`Удалить ${forumThread ? 'тему' : 'ветку'} «${name}»`}
          text={forumThread ? "Сообщения темы будут удалены безвозвратно." : "Сообщения ветки будут удалены безвозвратно."}
          confirmLabel={forumThread ? "Удалить тему" : "Удалить ветку"}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={async () => {
            await deleteChannel(channelId)
            setConfirmDelete(false)
            if (parentId) openThread(parentId)
          }}
        />
      ) : null}
    </Root>
  )
}
